import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SUBJECT_AREA_PALETTE } from "../lib/subject-area-palette";
import { SubjectAreaColorPicker } from "./subject-area-color-picker";
import type {
  SubjectArea,
  SubjectAreaCreateRequest,
  SubjectAreaUpdateRequest,
} from "../types";

type SubjectAreaFormDialogProps = (
  | {
      mode: "create";
      area?: undefined;
      freshArea?: undefined;
      onCreate: (data: SubjectAreaCreateRequest) => Promise<void> | void;
      onUpdate?: undefined;
    }
  | {
      mode: "edit";
      area: SubjectArea;
      /**
       * Latest cached version of `area`, refetched while the dialog is open.
       * If it differs from the originally-opened area (by `updatedAt`) we show
       * a non-blocking "updated elsewhere" banner so the user can opt into
       * reseeding the form. Optional — falls back to `area` when absent.
       */
      freshArea?: SubjectArea;
      onCreate?: undefined;
      onUpdate: (data: SubjectAreaUpdateRequest) => Promise<void> | void;
    }
) & {
  open: boolean;
  onClose: () => void;
  isLoading?: boolean;
  error?: string | null;
};

/**
 * Sub-dialog for creating or editing a single Subject Area. Mirrors the
 * `RoomFormDialog` shape: owns its own form state so the parent
 * `EditSubjectAreasDialog` stays a thin orchestrator.
 */
export function SubjectAreaFormDialog(props: SubjectAreaFormDialogProps) {
  const { open, onClose, isLoading = false, error, mode, area } = props;
  // Snapshot `updatedAt` at mount so we can detect server-side changes that
  // landed while the dialog was open (lost-update soft warning).
  const [openedUpdatedAt] = useState<string | null>(area?.updatedAt ?? null);
  const [name, setName] = useState(area?.name ?? "");
  const [colorHex, setColorHex] = useState(
    area?.colorHex ?? SUBJECT_AREA_PALETTE[0].hex,
  );
  const [localError, setLocalError] = useState<string | null>(null);
  /** When true, the user dismissed the stale-data banner for this session. */
  const [staleBannerDismissed, setStaleBannerDismissed] = useState(false);

  const freshArea =
    props.mode === "edit" ? (props.freshArea ?? props.area) : null;
  const isStale =
    mode === "edit" &&
    !staleBannerDismissed &&
    freshArea != null &&
    openedUpdatedAt != null &&
    freshArea.updatedAt !== openedUpdatedAt;

  const handleReloadFromServer = () => {
    if (!freshArea) return;
    setName(freshArea.name);
    setColorHex(freshArea.colorHex);
    setStaleBannerDismissed(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setLocalError("Name is required.");
      return;
    }

    if (props.mode === "create") {
      await props.onCreate({ name: trimmedName, colorHex });
      return;
    }
    await props.onUpdate({ name: trimmedName, colorHex });
  };

  const displayError = localError ?? error ?? null;
  const title = mode === "create" ? "Add subject area" : "Edit subject area";
  const submitLabel =
    mode === "create" ? "Create subject area" : "Save changes";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      testId="subject-area-form-dialog"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {isStale && (
          <div
            role="status"
            data-testid="subject-area-form-stale-banner"
            className="flex items-center justify-between gap-3 px-3 py-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs"
          >
            <span>This subject area was updated elsewhere.</span>
            <button
              type="button"
              onClick={handleReloadFromServer}
              data-testid="subject-area-form-stale-reload"
              className="underline hover:no-underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            >
              Reload latest
            </button>
          </div>
        )}
        {displayError && (
          <div
            role="alert"
            data-testid="subject-area-form-error"
            className="px-3 py-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs"
          >
            {displayError}
          </div>
        )}
        <div>
          <Label htmlFor="saf-name">Name</Label>
          <Input
            id="saf-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="subject-area-name-input"
          />
        </div>
        <div>
          <Label htmlFor="saf-color">Color</Label>
          <div id="saf-color" className="mt-1">
            <SubjectAreaColorPicker
              colorHex={colorHex}
              onChange={setColorHex}
              testIdPrefix="subject-area-form-color"
              size="md"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            data-testid="subject-area-form-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            data-testid="subject-area-form-submit"
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
